"use client";

import { useCallback, useEffect, useRef } from "react";

import type { ProductListItem } from "@/shared/api/services/productService";

import { useRecommendedFeed } from "@/features/home/queries/useHomeData";
import { ProductCard, ProductCardSkeleton } from "@/features/shopping/components/ProductCard";

export function RecommendedFeed() {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useRecommendedFeed();

  const observerRef = useRef<HTMLDivElement>(null);

  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  useEffect(() => {
    const el = observerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(handleIntersect, {
      rootMargin: "400px",
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleIntersect]);

  const products: ProductListItem[] = (() => {
    const all = data?.pages.flatMap((page) => page?.data?.products?.list ?? []) ?? [];
    const seen = new Set<number>();
    return all.filter((p) => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
  })();

  return (
    <section className="py-10 md:py-14 border-t border-neutral-100">
      <div className="max-w-screen-xl mx-auto px-6">
        <h2 className="font-heading text-base md:text-lg font-semibold tracking-tight mb-6 md:mb-8">
          추천 상품
        </h2>

        {isLoading ? (
          <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-5">
            {Array.from({ length: 12 }).map((_, i) => (
              <ProductCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-5">
              {products.map((product, index) => (
                <ProductCard key={`${product.id}-${index}`} product={product} />
              ))}
            </div>

            {/* Infinite scroll trigger */}
            <div ref={observerRef} className="h-1" />

            {isFetchingNextPage && (
              <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-5 mt-8">
                {Array.from({ length: 8 }).map((_, i) => (
                  <ProductCardSkeleton key={i} />
                ))}
              </div>
            )}

            {!hasNextPage && products.length > 0 && (
              <p className="text-center text-xs text-neutral-300 mt-12 pb-4">
                모든 상품을 확인했습니다
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
